import type { WorkspaceMenuItemResponse } from '@protoss/shared';
import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';

interface UseWorkspaceMenuResult {
  items: WorkspaceMenuItemResponse[];
  isLoading: boolean;
  error: string | null;
}

// Module-level cache so the menu doesn't re-fetch on every mount (e.g. navigating
// between AdministrationPage and ProductAdminPage).  The cache is intentionally
// session-scoped (cleared on full page reload) since menu items only change after
// workspace admin actions that trigger a new page load anyway.
let menuCache: WorkspaceMenuItemResponse[] | null = null;
let inflightPromise: Promise<WorkspaceMenuItemResponse[]> | null = null;

/**
 * useWorkspaceMenu - fetch the dynamic workspace administration menu.
 *
 * Calls GET /api/workspace/menu, which returns workspace menu items with
 * entitlement and role state resolved server-side.
 *
 * Non-admin users receive an empty items array (the route applies role
 * filtering before responding).  The hook does not make assumptions about
 * whether the current user is an admin.
 *
 * Authorization: Firebase ID token is attached to the request.
 */
export function useWorkspaceMenu(): UseWorkspaceMenuResult {
  const [items, setItems] = React.useState<WorkspaceMenuItemResponse[]>(menuCache ?? []);
  const [isLoading, setIsLoading] = React.useState(menuCache === null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Already cached - nothing to do, state was seeded from cache above.
    if (menuCache !== null) return;

    // Deduplicate concurrent fetches (e.g. two components mounting simultaneously).
    if (!inflightPromise) {
      inflightPromise = apiFetch<{ items: WorkspaceMenuItemResponse[] }>('/api/workspace/menu')
        .then((data) => {
          menuCache = data.items;
          inflightPromise = null;
          return data.items;
        })
        .catch((err) => {
          inflightPromise = null;
          throw err;
        });
    }

    inflightPromise
      .then((fetchedItems) => {
        setItems(fetchedItems);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to fetch workspace menu');
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { items, isLoading, error };
}
